class ByteQuotePdfGenerator {
  constructor(options = {}) {
    this.pageWidth = 595.28;
    this.pageHeight = 841.89;
    this.margin = 28;
    this.logoUrl = options.logoUrl || '';
    this.labels = options.labels || {};
  }

  async download(quote, filename) {
    const logoData = this.logoUrl ? await this.#loadImageData(this.logoUrl) : null;
    const html = this.#buildHtml(quote, logoData);
    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      window.alert('PDF penceresi açılamadı. Lütfen açılır pencere engelleyicisini kapatın.');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.document.title = filename.replace(/\.pdf$/i, '');

    await new Promise((resolve) => {
      printWindow.onload = resolve;
      setTimeout(resolve, 600);
    });

    printWindow.focus();
    printWindow.print();
  }

  async #loadImageData(url) {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  }

  #formatMoney(amount) {
    return `${Number(amount).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;
  }

  #formatDate(dateValue) {
    if (!dateValue) return '';
    const parts = dateValue.split('-');
    if (parts.length === 3) {
      return `${parts[2]}.${parts[1]}.${parts[0]}`;
    }
    return dateValue;
  }

  #escapeHtml(text) {
    return String(text || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  #buildHtml(quote, logoData) {
    const rows = quote.items
      .map(
        (item) => `
        <tr>
          <td class="image-cell">
            ${item.image ? `<img src="${this.#escapeHtml(item.image)}" alt="">` : ''}
          </td>
          <td>${this.#escapeHtml(item.title)}</td>
          <td class="num">${this.#formatMoney(item.unitPriceIncVat)}</td>
          <td class="num">${item.quantity}</td>
          <td></td>
          <td class="desc">${this.#escapeHtml(item.description)}</td>
          <td class="num">${item.vatRate.toFixed(2)}%</td>
          <td class="num">${this.#formatMoney(item.subtotalExVat)}</td>
          <td class="num">${this.#formatMoney(item.subtotalIncVat)}</td>
        </tr>`
      )
      .join('');

    return `<!DOCTYPE html>
<html lang="tr">
<head>
  <meta charset="utf-8">
  <title>${this.#escapeHtml(quote.pdfTitle)}</title>
  <style>
    @page { size: A4; margin: 12mm; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 11px;
      color: #111;
    }
    .header {
      display: grid;
      grid-template-columns: 120px 1fr 120px;
      align-items: center;
      margin-bottom: 18px;
      min-height: 70px;
    }
    .header__logo img {
      max-width: 110px;
      max-height: 60px;
      object-fit: contain;
    }
    .header__title {
      margin: 0;
      text-align: center;
      font-size: 22px;
      font-weight: 700;
    }
    .meta {
      margin-bottom: 14px;
      line-height: 1.6;
    }
    .meta p { margin: 0; }
    table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
    }
    th, td {
      border: 1px solid #222;
      padding: 6px 4px;
      vertical-align: top;
      word-wrap: break-word;
    }
    th {
      font-size: 10px;
      text-align: center;
      background: #f5f5f5;
    }
    td.num { text-align: right; white-space: nowrap; }
    td.desc { font-size: 10px; }
    td.image-cell { width: 42px; text-align: center; }
    td.image-cell img { width: 36px; height: 36px; object-fit: cover; }
    .totals {
      margin-top: 18px;
      margin-left: auto;
      width: 280px;
    }
    .totals h3 {
      margin: 0 0 8px;
      font-size: 13px;
      text-align: right;
    }
    .totals p {
      margin: 0 0 4px;
      text-align: right;
      font-size: 11px;
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="header__logo">
      ${logoData ? `<img src="${logoData}" alt="">` : ''}
    </div>
    <h1 class="header__title">${this.#escapeHtml(quote.pdfTitle)}</h1>
    <div></div>
  </div>

  <div class="meta">
    <p><strong>${this.#escapeHtml(this.labels.quoteDate || 'Teklif Tarihi')}:</strong> ${this.#formatDate(quote.quoteDate)}</p>
    <p><strong>${this.#escapeHtml(this.labels.quoteProvider || 'Teklif Veren')}:</strong> ${this.#escapeHtml(quote.quoteProvider)}</p>
    ${quote.quoteNote ? `<p><strong>${this.#escapeHtml(this.labels.quoteNote || 'Teklif Notu')}:</strong> ${this.#escapeHtml(quote.quoteNote)}</p>` : ''}
  </div>

  <table>
    <thead>
      <tr>
        <th style="width:42px">${this.#escapeHtml(this.labels.colImage || 'Resim')}</th>
        <th style="width:16%">${this.#escapeHtml(this.labels.colName || 'Ürün Adı')}</th>
        <th style="width:9%">${this.#escapeHtml(this.labels.colPrice || 'Fiyat')}</th>
        <th style="width:5%">${this.#escapeHtml(this.labels.colQty || 'Adet')}</th>
        <th style="width:10%">${this.#escapeHtml(this.labels.colPrint || 'Baskı / Açıklama')}</th>
        <th style="width:18%">${this.#escapeHtml(this.labels.colDescription || 'Açıklama')}</th>
        <th style="width:7%">${this.#escapeHtml(this.labels.colVat || 'KDV (%)')}</th>
        <th style="width:11%">${this.#escapeHtml(this.labels.colExVat || 'KDV Hariç Toplam')}</th>
        <th style="width:11%">${this.#escapeHtml(this.labels.colIncVat || 'KDV Dahil Toplam')}</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>

  <div class="totals">
    <h3>${this.#escapeHtml(this.labels.totalsTitle || 'Toplamlar')}</h3>
    <p>${this.#escapeHtml(this.labels.vat10 || '%10 KDV Tutarı')}: ${this.#formatMoney(quote.totals.vat10Amount)}</p>
    <p>${this.#escapeHtml(this.labels.vat20 || '%20 KDV Tutarı')}: ${this.#formatMoney(quote.totals.vat20Amount)}</p>
    <p>${this.#escapeHtml(this.labels.totalVat || 'Toplam KDV Tutarı')}: ${this.#formatMoney(quote.totals.totalVatAmount)}</p>
    ${
      quote.totals.discountPercent > 0
        ? `<p>${this.#escapeHtml(this.labels.discount || 'İskonto')} (%${quote.totals.discountPercent}): -${this.#formatMoney(quote.totals.discountAmountIncVat)}</p>`
        : ''
    }
    <p>${this.#escapeHtml(this.labels.grandExVat || 'KDV Hariç Toplam')}: ${this.#formatMoney(quote.totals.grandTotalExVat)}</p>
    <p>${this.#escapeHtml(this.labels.grandIncVat || 'KDV Dahil Toplam')}: ${this.#formatMoney(quote.totals.grandTotalIncVat)}</p>
  </div>
</body>
</html>`;
  }
}
